import React, { useEffect, useState } from 'react';
import Classnames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { PagedListItemProps } from './paged-list-item-wrapper';
import { useNumberSequence } from '../../../hooks';
import { PagedListStateActionNames } from './paged-list-reducer';
import { PagedListStateManager } from './use-paged-list-state';
import { PagedListWrapper } from './paged-list-wrapper';
import { EmptyDataList } from '../../../modules/partner-portal/components';
import { PagedListContentList } from './paged-list-content-list';

import './paged-list.scss';

interface PagedList<RowType = string[]> {
    // Page retrieval
    getNextPage: () => Promise<void>;

    // Row component
    ListItemComponent: React.ComponentType<PagedListItemProps<RowType>>;

    // Custom components
    CustomLoaderComponent?: React.ComponentType;
    EmptyListComponent?: React.ComponentType;
    ErrorComponent?: React.ComponentType;
    pageSelectorComponent?: JSX.Element;
    ListHeader?: React.ComponentType;

    // Callbacks
    atEnd?: () => void;
    onError?: (err: Error) => void;
    onFirstPage?: (err?: Error) => boolean;

    // Config items
    dense?: boolean;
    idBase?: string;
    pageLength?: number;
    shouldShowLogo?: boolean;
    isGrid?: boolean;
    pageSelectorOnBottom?: boolean;
    disableScroll?: boolean;
    disableInfiniteScroll?: boolean;

    // State management
    pagingStateManager: PagedListStateManager<RowType>;
}

export enum InfiniteScrollState {
    EMPTY = 'empty',
    ERROR = 'error',
    LOADING = 'loading',
    RESOLVED = 'resolved',
}

export function PagedList<RowType>({
    // Page retrieval
    getNextPage,

    // List Header component
    ListHeader,

    // Row component
    ListItemComponent,

    // Custom components
    CustomLoaderComponent,
    EmptyListComponent,
    ErrorComponent,
    pageSelectorComponent,

    // Callbacks
    atEnd,
    onError,
    onFirstPage,

    // Config items
    dense = false,
    idBase = 'pagedList',
    pagingStateManager: localStateManager,
    pageLength = 50,
    shouldShowLogo,
    isGrid = false,
    pageSelectorOnBottom = false,
    disableScroll = false,
    disableInfiniteScroll = false,
}: PagedList<RowType>): React.ReactElement {
    const [listId] = useState(idBase + (Date.now() % 1000).toString().padStart(4, '0'));
    const [localState, dispatchLocalState] = localStateManager;
    const loadingKeys = useNumberSequence();

    const parentListClassnames = Classnames('paged-list', {
        'paged-list--no-scroll': disableScroll,
    });

    const skeletonClassnames = Classnames('paged-list__loading-item', {
        'paged-list__loading-item--top': localState.isLoading,
    });

    const listLoadingClassnames = Classnames('paged-list__loading', {
        'paged-list__loading--grid': isGrid,
    });

    useEffect(() => {
        if (localState.atEnd) {
            atEnd?.call(null);
        }
    }, [localState.atEnd]);

    useEffect(() => {
        if (localState.isError) {
            onError?.call(null);
        }
    }, [localState.isError]);

    useEffect(() => {
        if (localState.needsPage) {
            getMoreData().catch((e) => {
                console.error('getMoreData call fail', e);
            });
            return;
        }

        if (localState.pageManager.length === 0 && !localState.isEmpty) {
            dispatchLocalState({ type: PagedListStateActionNames.SET_IS_EMPTY });
        }
    }, [localState.needsPage]);

    const getMoreData = async () => {
        if (localState.atEnd || localState.isLoading) {
            if (localState.atEnd && localState.needsPage) {
                dispatchLocalState({ type: PagedListStateActionNames.UNSET_NEEDS_PAGE });
            }

            return;
        }

        dispatchLocalState({ type: PagedListStateActionNames.SET_LOADING });

        try {
            await getNextPage();

            if (localState.isFirstPage) {
                onFirstPage?.call(null, null);
            }
        } catch (e) {
            let shouldToast = true;
            console.error('Get next page error', e);

            if (localState.isFirstPage && onFirstPage) {
                shouldToast = onFirstPage(e);
            }

            if (shouldToast) {
                toast.error('There was an error loading your items');
            }

            await dispatchLocalState({
                type: PagedListStateActionNames.SET_IS_ERROR,
            });
        }
    };

    const renderLoadingTop = () => {
        if (localState.pageManager.length === 0 && localState.isLoading) {
            return (
                <ul className={listLoadingClassnames}>
                    {new Array(pageLength).fill('').map(() => {
                        return (
                            <li key={loadingKeys()}>
                                {CustomLoaderComponent ? (
                                    <CustomLoaderComponent />
                                ) : (
                                    <Skeleton containerTestId="paged-list-skeleton" height={44} />
                                )}
                            </li>
                        );
                    })}
                </ul>
            );
        }

        return null;
    };

    const renderLoadingBottom = () => {
        if (localState.pageManager.length === 0 || !localState.isLoading) {
            return null;
        }

        if (CustomLoaderComponent) {
            return <CustomLoaderComponent />;
        }

        return (
            <div className={skeletonClassnames}>
                <Skeleton containerTestId="paged-list-skeleton" height={44} />
            </div>
        );
    };

    const renderErrorComponent = () => {
        if (!localState.isError) {
            return null;
        }

        if (ErrorComponent) {
            return <ErrorComponent />;
        }
    };

    if (localState.isEmpty) {
        if (EmptyListComponent) {
            return <EmptyListComponent />;
        } else {
            return <EmptyDataList />;
        }
    }

    return (
        <PagedListWrapper>
            {!pageSelectorOnBottom && pageSelectorComponent}
            {ListHeader && <ListHeader />}
            <div className={parentListClassnames} data-listid={listId}>
                {renderLoadingTop()}
                <PagedListContentList<RowType>
                    listId={listId}
                    isGrid={isGrid}
                    dense={dense}
                    disableInfiniteScroll={disableInfiniteScroll}
                    shouldShowLogo={shouldShowLogo}
                    ListItemComponent={ListItemComponent}
                    pagingStateManager={localStateManager}
                />
                {renderLoadingBottom()}
                {renderErrorComponent()}
            </div>
            {pageSelectorOnBottom && pageSelectorComponent}
        </PagedListWrapper>
    );
}
